import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { ScanBarcode, TrendingDown, Bell, MapPin, Sparkles, ShoppingBasket, BarChart3, Bot, Check, ArrowRight, Megaphone, Store } from "lucide-react";

export const Route = createFileRoute("/productos")({
  head: () => ({
    meta: [
      { title: "Productos y Servicios — PRICO" },
      { name: "description", content: "App PRICO gratuita, plan Premium con Asistente IA y soluciones para comercios y anunciantes." },
      { property: "og:title", content: "Productos y Servicios — PRICO" },
      { property: "og:description", content: "Descubre PRICO Free, Premium, Business y Ads." },
      { property: "og:url", content: "/productos" },
    ],
    links: [{ rel: "canonical", href: "/productos" }],
  }),
  component: ProductsPage,
});

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
};

function ProductsPage() {
  const features = [
    { i: ScanBarcode, t: "Escaneo de códigos de barra", d: "Agrega productos en segundos con la cámara." },
    { i: TrendingDown, t: "Historial de precios", d: "Mira cómo evoluciona el precio por tienda y fecha." },
    { i: Bell, t: "Alertas inteligentes", d: "Recibe notificaciones cuando baje el precio." },
    { i: MapPin, t: "Ruta de compra", d: "Plan organizado entre tiendas para tu mercado." },
    { i: ShoppingBasket, t: "Listas por rubro", d: "Mercado, medicinas, limpieza... todo en su lugar." },
    { i: BarChart3, t: "Estadísticas", d: "Sabe cuánto ahorras cada semana y cada mes." },
  ];

  const plans = [
    {
      name: "Free",
      price: "$0",
      desc: "Para empezar a ahorrar hoy mismo.",
      bullets: ["Registro ilimitado de precios", "Comparativas básicas", "Listas de compras", "Comunidad PRICO"],
      cta: "Empieza gratis",
      featured: false,
    },
    {
      name: "Premium",
      price: "USD 5",
      per: "/mes",
      desc: "Tu asistente IA personal de compras.",
      bullets: ["Todo lo de Free", "Asistente IA Premium", "Ruta de compra optimizada", "Alertas geolocalizadas", "Sin publicidad"],
      cta: "Probar Premium",
      featured: true,
    },
    {
      name: "Business",
      price: "Custom",
      desc: "Para comercios y anunciantes.",
      bullets: ["Estadísticas de intención de compra", "Espacios publicitarios segmentados", "Tendencias de búsqueda", "API y reportes"],
      cta: "Contactar ventas",
      featured: false,
    },
  ];

  return (
    <>
      <section className="container-prico py-20 md:py-28 text-center max-w-3xl mx-auto">
        <motion.div {...fadeUp}>
          <span className="chip">Producto</span>
          <h1 className="text-5xl md:text-7xl font-black mt-5 leading-[1]">
            Una app. <span style={{ background: "var(--gradient-primary)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Mil formas</span> de ahorrar.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground">
            PRICO funciona en Android, iOS y web. Conecta a usuarios, comercios y anunciantes
            alrededor del precio más justo.
          </p>
        </motion.div>
      </section>

      <section className="container-prico pb-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div key={f.t} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}
              className="card-soft p-7 hover:-translate-y-1 transition">
              <div className="size-12 rounded-2xl bg-primary/10 text-primary grid place-items-center">
                <f.i className="size-6" />
              </div>
              <h3 className="font-bold text-lg mt-4">{f.t}</h3>
              <p className="text-sm text-muted-foreground mt-1">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-20" style={{ background: "var(--gradient-warm)" }}>
        <div className="container-prico">
          <motion.div {...fadeUp} className="text-center mb-14 max-w-2xl mx-auto">
            <span className="chip">Planes</span>
            <h2 className="text-4xl md:text-5xl font-black mt-4">Para cada bolsillo y cada negocio</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {plans.map((p, i) => (
              <motion.div key={p.name} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className={`relative card-soft p-8 ${p.featured ? "ring-2 ring-primary scale-[1.02]" : ""}`}
                style={p.featured ? { background: "var(--ink)", color: "white" } : {}}>
                {p.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 chip" style={{ background: "var(--primary)", color: "white", borderColor: "transparent" }}>
                    <Sparkles className="size-3.5" /> Más popular
                  </span>
                )}
                <h3 className="text-2xl font-black">{p.name}</h3>
                <p className={`text-sm mt-1 ${p.featured ? "text-white/70" : "text-muted-foreground"}`}>{p.desc}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-black">{p.price}</span>
                  {p.per && <span className={p.featured ? "text-white/70" : "text-muted-foreground"}>{p.per}</span>}
                </div>
                <ul className="mt-6 space-y-2.5">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm">
                      <Check className={`size-4 mt-0.5 shrink-0 ${p.featured ? "text-primary" : "text-primary"}`} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contacto" className={p.featured ? "btn-primary w-full mt-8 text-sm" : "btn-ghost w-full mt-8 text-sm"}>
                  {p.cta} <ArrowRight className="size-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-prico py-24 grid md:grid-cols-3 gap-6">
        {[
          { i: Bot, t: "Asistente IA", d: "Sugerencias inteligentes y rutas optimizadas." },
          { i: Store, t: "Para comercios", d: "Llega a clientes con intención real de compra." },
          { i: Megaphone, t: "Para anunciantes", d: "Alertas de oferta segmentadas por producto y zona." },
        ].map((f, i) => (
          <motion.div key={f.t} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
            className="card-soft p-8">
            <div className="size-14 rounded-2xl grid place-items-center text-primary-foreground" style={{ background: "var(--gradient-primary)" }}>
              <f.i className="size-7" />
            </div>
            <h3 className="text-2xl font-bold mt-5">{f.t}</h3>
            <p className="text-muted-foreground text-sm mt-2">{f.d}</p>
          </motion.div>
        ))}
      </section>
    </>
  );
}
